import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Save, X, FileText, User, MapPin,
  Calendar, Hash, DollarSign, Percent, Calculator,
  Building, Phone, Mail, Search, ChevronDown, CheckCircle, Send, Plus, Trash2,
  HelpCircle
} from 'lucide-react';
import Breadcrumb from '../../components/Common/Breadcrumb';
import HelpModal from '../../components/HelpModal/HelpModal';
import CustomerSelectionModal from '../../components/CustomerSelectionModal/CustomerSelectionModal';
import ServiceSelectionTable from '../../components/ServiceSelectionTable/ServiceSelectionTable';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { toast } from 'react-toastify';
import autoIncrementService from '../../services/autoIncrementService';
import companyService from '../../services/companyService';
import salesQuotationService from '../../services/salesQuotationService';
import paymentTermsMasterService from '../../services/paymentTermsMasterService';
import { calculateInvoiceGST } from '../../utils/gstCalculator';

const SalesQuotationForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = id && id !== 'new';
  const isView = window.location.pathname.includes('/view/');
  const isApprove = window.location.pathname.includes('/approve/');
  const isSend = window.location.pathname.includes('/send/');

  const [activeTab, setActiveTab] = useState('quotationDetails');
  const [showHelp, setShowHelp] = useState(false);
  const [formData, setFormData] = useState({
    // Quotation Details
    quotationNumber: '',
    quotationDate: new Date().toISOString().split('T')[0],
    validUntil: (() => {
      const date = new Date();
      date.setDate(date.getDate() + 30);
      return date.toISOString().split('T')[0];
    })(),
    revisionNumber: 0,
    
    // Customer Details
    customerId: '',
    customerName: '',
    customerCode: '',
    customerCategory: '',
    customerAddress: '',
    customerCity: '',
    customerState: '',
    customerPincode: '',
    customerPhone: '',
    customerEmail: '',
    customerGSTIN: '',
    customerPaymentTerms: '',
    currency: '',
    
    // Reference Details
    referenceNumber: '', // Customer reference/PO number
    referenceModel: 'Customer',
    
    // Item Details
    items: [],
    
    // GST Details
    cgstRate: 9,
    sgstRate: 9,
    igstRate: 18,
    cgstAmount: 0,
    sgstAmount: 0,
    igstAmount: 0,
    totalTaxAmount: 0,
    subtotal: 0,
    totalAmount: 0,
    placeOfSupply: '',
    
    // Additional Details
    notes: '',
    terms: [], // Array of terms for bullet points
    status: 'Draft',
    remark: '',
    totalValue: 0.00
  });

  const [loading, setLoading] = useState(false);
  const [isLoadingQuotationNumber, setIsLoadingQuotationNumber] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [serviceData, setServiceData] = useState([]);
  const [showServiceTable, setShowServiceTable] = useState(false);
  const [companyGSTIN, setCompanyGSTIN] = useState('');
  const [companyState, setCompanyState] = useState('');
  const [customerGSTIN, setCustomerGSTIN] = useState('');
  const [paymentTermsOptions, setPaymentTermsOptions] = useState([]);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const serviceTableRef = useRef(null);
  const [canCreateQuotation, setCanCreateQuotation] = useState(false);
  const [newTerm, setNewTerm] = useState('');

  // Default Terms & Conditions
  const DEFAULT_TERMS_AND_CONDITIONS = [
    'Quotation is valid for 30 days from the date of issue.',
    'Prices are exclusive of GST and applicable taxes.',
    'Payment terms: Within 30 days from invoice date.',
    '18% p.a. interest applicable on delayed payments.',
    'Work will commence only after receipt of Purchase Order.',
    'Quotation covers only listed scope; additional work charged separately.',
    'Cancellation after confirmation may attract cancellation charges.',
    'All disputes are subject to Nagpur jurisdiction.',
    'This quotation is confidential and intended only for the recipient.',
    'Acceptance of quotation implies acceptance of these Terms & Conditions.'
  ];

  // Load company GSTIN and payment terms on component mount
  useEffect(() => {
    const fetchCompanyGSTIN = async () => {
      try {
        const response = await companyService.getCompanyDetails();
        if (response && response.success && response.data) {
          if (response.data.GSTIN) setCompanyGSTIN(response.data.GSTIN);
          if (response.data.companyBillingAddress?.state) {
            setCompanyState(response.data.companyBillingAddress.state);
          }
        }
      } catch (error) {
        console.error('Error fetching company GSTIN:', error);
        if (error.response?.status === 401) {
          toast.error('Please log in to continue');
          navigate('/login');
          return;
        }
        const storedGSTIN = localStorage.getItem('companyGSTIN');
        if (storedGSTIN) {
          setCompanyGSTIN(storedGSTIN);
        } else {
          setCompanyGSTIN('27AAGCI0729M1ZB');
        }
      }
    };

    const fetchPaymentTerms = async () => {
      try {
        const response = await paymentTermsMasterService.getActivePaymentTerms();
        if (response && response.success) {
          setPaymentTermsOptions(response.data || []);
        }
      } catch (error) {
        console.error('Error fetching payment terms:', error);
      }
    };

    fetchCompanyGSTIN();
    fetchPaymentTerms();
  }, [navigate]);

  // Initialize default terms and validUntil for new quotations
  useEffect(() => {
    if ((!id || id === 'new') && formData.terms.length === 0) {
      const quotationDate = new Date(formData.quotationDate);
      const validUntilDate = new Date(quotationDate);
      validUntilDate.setDate(validUntilDate.getDate() + 30);
      
      setFormData(prev => ({
        ...prev,
        terms: [...DEFAULT_TERMS_AND_CONDITIONS],
        validUntil: validUntilDate.toISOString().split('T')[0]
      }));
    }
  }, [id]);

  // Load data for edit/view/approve/send mode
  useEffect(() => {
    if (isEdit || isView || isApprove || isSend) {
      loadQuotationData();
    } else {
      // Don't generate quotation number on page load - it will be generated on save
      // Just show placeholder
      setFormData(prev => ({
        ...prev,
        quotationNumber: 'Auto-generated on save'
      }));
    }
  }, [id, isEdit, isView, isApprove, isSend]);

  // Trigger GST calculation when switching to GST Details tab or when GSTINs change
  useEffect(() => {
    if (activeTab === 'gstDetails' && formData.totalValue > 0) {
      calculateGST(formData.totalValue);
    }
  }, [activeTab, formData.totalValue, companyGSTIN, customerGSTIN, formData.placeOfSupply, formData.customerCategory, companyState]);

  // F1 key handler for test data population
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only work in create mode (not edit/view/approve/send)
      if (e.key === 'F1' && !isEdit && !isView && !isApprove && !isSend) {
        e.preventDefault();
        populateTestData();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEdit, isView, isApprove, isSend]);

  // Initialize default terms for new quotations
  useEffect(() => {
    if ((!id || id === 'new') && formData.terms.length === 0) {
      setFormData(prev => ({
        ...prev,
        terms: [...DEFAULT_TERMS_AND_CONDITIONS]
      }));
    }
  }, [id]);

  const loadQuotationData = async () => {
    setLoading(true);
    try {
      if (id && id !== 'new') {
        const response = await salesQuotationService.getSalesQuotationById(id);
        if (response && response.success && response.data) {
          const quotation = response.data;
          
          // Format date fields
          const quotationDate = quotation.quotationDate 
            ? new Date(quotation.quotationDate).toISOString().split('T')[0] 
            : new Date().toISOString().split('T')[0];
          const validUntil = quotation.validUntil 
            ? new Date(quotation.validUntil).toISOString().split('T')[0] 
            : '';
          
          const currency = quotation.currency || 
                          (quotation.customer && quotation.customer.customerCurrency) || 
                          '';
          
          // Extract GST rates
          let cgstRate = 9;
          let sgstRate = 9;
          let igstRate = 18;
          
          if (quotation.serviceDetails && quotation.serviceDetails.length > 0) {
            const firstService = quotation.serviceDetails[0];
            cgstRate = firstService.cgstRate || 9;
            sgstRate = firstService.sgstRate || 9;
            igstRate = firstService.igstRate || 18;
          }
          
          setFormData({
            quotationNumber: quotation.quotationNumber || '',
            quotationDate: quotationDate,
            validUntil: validUntil,
            revisionNumber: quotation.revisionNumber || 0,
            customerId: quotation.customer?._id || quotation.customer || '',
            customerName: quotation.customerName || '',
            customerCode: quotation.customerCode || (quotation.customer?.customerCode || ''),
            customerCategory: quotation.customerCategory || 'Domestic',
            customerGSTIN: quotation.customerGSTIN || (quotation.customer?.GSTIN || ''),
            customerPaymentTerms: quotation.customerPaymentTerms || '',
            currency: currency,
            referenceNumber: quotation.referenceNumber || '',
            referenceModel: quotation.referenceModel || 'Customer',
            items: quotation.serviceDetails?.map((service, index) => ({
              id: service._id || service.service?._id || index + 1,
              serviceId: service.service?._id || service.service || '',
              itemCode: service.serviceCode || '',
              itemName: service.serviceDescription || '',
              serviceDescription: service.serviceDescription || '',
              description: service.serviceDescription || '',
              quantity: service.qty || 0,
              unit: 'Nos',
              rate: service.unitRate || 0,
              amount: service.lineValue || 0,
              discount: 0,
              taxableAmount: service.lineValue || 0,
              lineValue: service.lineValue || 0,
              cgstRate: service.cgstRate || cgstRate,
              sgstRate: service.sgstRate || sgstRate,
              igstRate: service.igstRate || igstRate,
              SACCode: service.SACCode || '',
              selectSAC: service.SACCode || ''
            })) || [],
            totalValue: quotation.totalValue || 0,
            cgstAmount: quotation.totalCGSTAmount || 0,
            sgstAmount: quotation.totalSGSTAmount || 0,
            igstAmount: quotation.totalIGSTAmount || 0,
            totalTaxAmount: quotation.totalTaxAmount || 0,
            totalAmount: quotation.totalAmountWithTax || 0,
            subtotal: quotation.totalValue || 0,
            cgstRate: cgstRate,
            sgstRate: sgstRate,
            igstRate: igstRate,
            notes: quotation.remarks || '',
            terms: Array.isArray(quotation.termsAndConditions) 
              ? quotation.termsAndConditions 
              : (quotation.paymentTerms 
                  ? (typeof quotation.paymentTerms === 'string' && quotation.paymentTerms.includes('\n')
                      ? quotation.paymentTerms.split('\n').filter(t => t.trim())
                      : [quotation.paymentTerms].filter(t => t.trim()))
                  : []),
            status: quotation.status || 'Draft'
          });
          
          const customerGstin = quotation.customerGSTIN || (quotation.customer?.GSTIN || '');
          if (customerGstin) {
            setCustomerGSTIN(customerGstin);
          }
          
          // Projects not needed for quotations
          
          if (quotation.serviceDetails && quotation.serviceDetails.length > 0) {
            const transformedServiceData = quotation.serviceDetails.map((service, index) => ({
              ...service.service,
              _id: service.service?._id || service._id || service.service,
              serviceId: service.service?._id || service.service || service._id,
              serviceCode: service.serviceCode || service.service?.serviceCode || '',
              serviceDescription: service.serviceDescription || service.service?.serviceDescription || '',
              selectSAC: service.SACCode || service.service?.selectSAC || '',
              SACCode: service.SACCode || service.service?.selectSAC || '',
              gstRate: service.gstRate || service.service?.gstRate || 0,
              cgstRate: service.cgstRate || cgstRate,
              sgstRate: service.sgstRate || sgstRate,
              igstRate: service.igstRate || igstRate,
              quantity: service.qty || 0,
              qty: service.qty || 0,
              unitRate: service.unitRate || 0,
              rate: service.unitRate || 0,
              lineValue: service.lineValue || 0,
              additionalInfo: service.additionalInfo || '',
              serialNumber: index + 1
            }));
            setServiceData(transformedServiceData);
            setShowServiceTable(true);
          } else {
            setServiceData([]);
            setShowServiceTable(false);
          }
        } else {
          toast.error('Failed to load quotation data');
        }
      }
    } catch (error) {
      console.error('Error loading quotation data:', error);
      toast.error('Failed to load quotation data');
    } finally {
      setLoading(false);
    }
  };

  const generateQuotationNumber = async () => {
    setIsLoadingQuotationNumber(true);
    try {
      const response = await autoIncrementService.getCurrentValue('SALES-QUOTATION');
      
      let sequence = null;
      if (response.success && response.data) {
        sequence = response.data;
      } else if (response.data) {
        sequence = response.data;
      } else if (response) {
        sequence = response;
      }
      
      if (sequence && sequence.formattedValue) {
        setFormData(prev => ({
          ...prev,
          quotationNumber: sequence.formattedValue
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          quotationNumber: 'SQ-2025-001'
        }));
      }
    } catch (error) {
      console.error('Error generating quotation number:', error);
      if (error.response?.status === 401) {
        toast.error('Please log in to continue');
        navigate('/login');
        return;
      }
      setFormData(prev => ({
        ...prev,
        quotationNumber: 'SQ-2025-001'
      }));
    } finally {
      setIsLoadingQuotationNumber(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: value
      };
      
      // Auto-update validUntil to 30 days from quotation date when quotation date changes
      if (name === 'quotationDate' && value) {
        const quotationDate = new Date(value);
        if (!isNaN(quotationDate.getTime())) {
          const validUntilDate = new Date(quotationDate);
          validUntilDate.setDate(validUntilDate.getDate() + 30);
          updated.validUntil = validUntilDate.toISOString().split('T')[0];
        }
      }
      
      return updated;
    });
  };

  // Terms & Conditions Management
  const addTerm = () => {
    if (newTerm.trim()) {
      setFormData(prev => ({
        ...prev,
        terms: [...(prev.terms || []), newTerm.trim()]
      }));
      setNewTerm('');
    }
  };

  const removeTerm = (index) => {
    setFormData(prev => ({
      ...prev,
      terms: prev.terms.filter((_, i) => i !== index)
    }));
  };

  const updateTerm = (index, value) => {
    setFormData(prev => ({
      ...prev,
      terms: prev.terms.map((term, i) => i === index ? value : term)
    }));
  };

  const handleTermKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      addTerm();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.customerId || !formData.customerName) {
      toast.error('Please select a customer');
      return;
    }
    
    if (!formData.customerCategory) {
      toast.error('Please select customer category');
      return;
    }
    
    if (!formData.customerPaymentTerms) {
      toast.error('Please select payment terms');
      return;
    }
    
    // Validate service details
    const validServices = formData.items.filter(item => {
      const qty = parseFloat(item.quantity || item.qty || 0);
      const rate = parseFloat(item.unitRate || item.rate || 0);
      return item.serviceId && item.SACCode && qty > 0 && rate > 0;
    });
    
    if (validServices.length === 0) {
      toast.error('Please add at least one service with quantity and rate');
      return;
    }
    
    setLoading(true);

    try {
      let paymentTermsDescription = '';
      if (formData.customerPaymentTerms) {
        const selectedTerm = paymentTermsOptions.find(
          term => (term._id || term.id) === formData.customerPaymentTerms
        );
        paymentTermsDescription = selectedTerm ? selectedTerm.description : '';
      }

      // Calculate validUntil date - default to 30 days from quotation date if not set
      let validUntilDate = formData.validUntil;
      if (!validUntilDate && formData.quotationDate) {
        const quotationDate = new Date(formData.quotationDate);
        quotationDate.setDate(quotationDate.getDate() + 30);
        validUntilDate = quotationDate.toISOString().split('T')[0];
      }

      const submissionData = {
        quotationDate: formData.quotationDate,
        validUntil: validUntilDate,
        revisionNumber: formData.revisionNumber || 0,
        customerCategory: formData.customerCategory,
        customerName: formData.customerName,
        customer: formData.customerId,
        customerCode: formData.customerCode,
        customerGSTIN: formData.customerGSTIN,
        customerPaymentTerms: paymentTermsDescription || formData.customerPaymentTerms,
        referenceNumber: formData.referenceNumber || '',
        referenceModel: formData.referenceModel || 'Customer',
        currency: formData.currency || 'INR',
        serviceDetails: formData.items.map(item => ({
          service: item.serviceId || item._id || item.id,
          serviceCode: item.serviceCode || item.itemCode || '',
          serviceDescription: item.serviceDescription || item.itemName || item.description || '',
          SACCode: item.SACCode || item.selectSAC || '',
          gstRate: item.gstRate || 0,
          igstRate: item.igstRate || formData.igstRate || 18,
          sgstRate: item.sgstRate || formData.sgstRate || 9,
          cgstRate: item.cgstRate || formData.cgstRate || 9,
          additionalInfo: item.additionalInfo || '',
          qty: item.quantity || item.qty || 0,
          unitRate: item.unitRate || item.rate || 0,
          lineValue: item.lineValue || (item.quantity * item.rate) || 0
        })).filter(item => item.service && item.SACCode && item.qty > 0 && item.unitRate > 0),
        termsAndConditions: Array.isArray(formData.terms) ? formData.terms : (formData.terms ? [formData.terms] : []),
        paymentTerms: paymentTermsDescription || formData.customerPaymentTerms,
        remarks: formData.remark || formData.notes
      };

      let response;
      if (isEdit) {
        response = await salesQuotationService.updateSalesQuotation(id, submissionData);
      } else {
        submissionData.status = 'Draft';
        response = await salesQuotationService.createSalesQuotation(submissionData);
      }

      if (response.success) {
        toast.success(isEdit ? 'Sales quotation updated successfully' : 'Sales quotation created successfully');
        navigate('/sales/sales-quotation');
      } else {
        toast.error(response.message || 'Failed to save sales quotation');
      }
    } catch (error) {
      console.error('Error saving sales quotation:', error);
      toast.error('Error saving sales quotation');
    } finally {
      setLoading(false);
    }
  };

  const populateTestData = async () => {
    try {
      // Set customer details (using test data)
      const testCustomerCode = 'C0001';
      const testCustomerName = 'Test Customer Private Limited';
      const testCustomerGSTIN = '27ABCDE1234F1Z5';
      
      // Set payment terms (use first available or default)
      const defaultPaymentTerms = paymentTermsOptions.length > 0 
        ? (paymentTermsOptions[0]._id || paymentTermsOptions[0].id)
        : '';

      // Update form data with test customer information
      setFormData(prev => ({
        ...prev,
        customerName: testCustomerName,
        customerCode: testCustomerCode,
        customerCategory: 'Domestic',
        customerGSTIN: testCustomerGSTIN,
        customerPaymentTerms: defaultPaymentTerms,
        currency: 'INR',
        referenceNumber: 'REF/2025/001',
        referenceModel: 'Customer',
        remark: 'Test quotation for demonstration purposes',
        terms: DEFAULT_TERMS_AND_CONDITIONS
      }));

      // Set customer GSTIN for GST calculation
      setCustomerGSTIN(testCustomerGSTIN);
      
      // Show service table so user can select services
      setShowServiceTable(true);

      toast.success('Test data populated! Customer details filled. Please select services from the table and enter quantities/rates.');
    } catch (error) {
      console.error('Error populating test data:', error);
      toast.error('Error populating test data');
    }
  };

  const handleCancel = () => {
    navigate('/sales/sales-quotation');
  };

  const handleApprove = () => {
    if (!id || id === 'new') {
      toast.error('Please save the quotation first before approving');
      return;
    }

    if (formData.status !== 'Draft') {
      toast.warning(`Quotation status is already ${formData.status}. Only Draft quotations can be approved.`);
      return;
    }

    setShowApproveModal(true);
  };

  const confirmApprove = async () => {
    setShowApproveModal(false);
    setLoading(true);
    try {
      const response = await salesQuotationService.updateSalesQuotation(id, {
        status: 'Approved'
      });

      if (response.success) {
        toast.success('Sales quotation approved successfully');
        navigate('/sales/sales-quotation');
      } else {
        toast.error(response.message || 'Failed to approve sales quotation');
      }
    } catch (error) {
      console.error('Error approving sales quotation:', error);
      toast.error('Failed to approve sales quotation');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = () => {
    if (!id || id === 'new') {
      toast.error('Please save the quotation first before sending');
      return;
    }

    if (formData.status !== 'Draft' && formData.status !== 'Approved') {
      toast.warning(`Quotation status is ${formData.status}. Only Draft or Approved quotations can be sent.`);
      return;
    }

    setShowSendModal(true);
  };

  const confirmSend = async () => {
    setShowSendModal(false);
    setLoading(true);
    try {
      const response = await salesQuotationService.updateSalesQuotation(id, {
        status: 'Sent'
      });

      if (response.success) {
        toast.success('Sales quotation sent successfully');
        navigate('/sales/sales-quotation');
      } else {
        toast.error(response.message || 'Failed to send sales quotation');
      }
    } catch (error) {
      console.error('Error sending sales quotation:', error);
      toast.error('Failed to send sales quotation');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerSearch = () => {
    setShowCustomerModal(true);
  };

  const handleCustomerSelect = (selectedItem) => {
    // Handle both customers and prospects
    const isProspect = selectedItem.type === 'Prospect' || selectedItem.prospectCode;
    const customerName = selectedItem.customerName || selectedItem.prospectName || selectedItem.name;
    const customerCode = selectedItem.customerCode || selectedItem.prospectCode || selectedItem.code;
    const customerCategory = selectedItem.customerCategory || selectedItem.prospectCategory || selectedItem.category;
    const gstin = selectedItem.GSTIN || selectedItem.gstin || '';
    const paymentTerms = selectedItem.customerPaymentTerms || selectedItem.paymentTerms || '';
    
    let defaultPaymentTerms = '';
    if (paymentTerms && paymentTermsOptions.length > 0) {
      const paymentTermsStr = paymentTerms.toString().trim();
      let matchedTerm = paymentTermsOptions.find(
        term => term.description && term.description.toString().trim() === paymentTermsStr
      );
      if (!matchedTerm) {
        matchedTerm = paymentTermsOptions.find(
          term => term.description && term.description.toString().trim().includes(paymentTermsStr)
        );
      }
      if (!matchedTerm) {
        matchedTerm = paymentTermsOptions.find(
          term => term.paymentTermsCode && term.paymentTermsCode.toString().trim() === paymentTermsStr
        );
      }
      if (matchedTerm) {
        defaultPaymentTerms = matchedTerm._id || matchedTerm.id;
      }
    }

    let category = 'Domestic';
    if (selectedItem.categoryType) {
      category = selectedItem.categoryType;
    } else if (customerCategory) {
      const cat = customerCategory.toString().trim();
      if (['Domestic', 'Export', 'SEZ', 'Deemed Export'].includes(cat)) {
        category = cat;
      } else if (cat.toLowerCase() === 'export' || cat.toLowerCase() === 'international') {
        category = 'Export';
      }
    }

    const billingAddr = selectedItem.customerBillingAddress?.[0]
      || selectedItem.prospectBillingAddress?.[0]
      || {};

    setFormData(prev => ({
      ...prev,
      customerId: selectedItem._id || selectedItem.id,
      customerName: customerName,
      customerCode: customerCode,
      customerGSTIN: gstin,
      placeOfSupply: billingAddr.state || selectedItem.prospectState || '',
      customerPaymentTerms: defaultPaymentTerms,
      customerCategory: category,
      currency: selectedItem.customerCurrency || selectedItem.currency || 'INR',
      referenceNumber: '',
      referenceModel: isProspect ? 'Prospect' : 'Customer'
    }));
    setCustomerGSTIN(gstin);
    setShowCustomerModal(false);
    setShowServiceTable(true);
    
    // Projects are not needed for quotations - removed
  };


  const handleServiceDataChange = (updatedServiceData) => {
    setServiceData(updatedServiceData);
    
    const totalValue = updatedServiceData.reduce((sum, service) => {
      const qty = parseFloat(service.quantity) || 0;
      const rate = parseFloat(service.unitRate) || 0;
      const lineValue = parseFloat(service.lineValue) || (qty * rate);
      return sum + lineValue;
    }, 0);
    
    const transformedItems = updatedServiceData
      .filter(service => {
        const qty = parseFloat(service.quantity) || 0;
        const rate = parseFloat(service.unitRate) || 0;
        return qty > 0 && rate > 0;
      })
      .map(service => ({
        id: service._id || service.id || service.serviceId,
        serviceId: service._id || service.id || service.serviceId,
        itemCode: service.serviceCode || '',
        itemName: service.serviceDescription || '',
        serviceDescription: service.serviceDescription || '',
        description: service.serviceDescription || '',
        SACCode: service.selectSAC || service.SACCode || '',
        selectSAC: service.selectSAC || service.SACCode || '',
        gstRate: service.gstRate || 0,
        igstRate: service.igstRate || formData.igstRate || 18,
        sgstRate: service.sgstRate || formData.sgstRate || 9,
        cgstRate: service.cgstRate || formData.cgstRate || 9,
        quantity: parseFloat(service.quantity) || 0,
        qty: parseFloat(service.quantity) || 0,
        unit: 'Nos',
        rate: parseFloat(service.unitRate) || 0,
        unitRate: parseFloat(service.unitRate) || 0,
        amount: parseFloat(service.lineValue) || (parseFloat(service.quantity) * parseFloat(service.unitRate)) || 0,
        lineValue: parseFloat(service.lineValue) || (parseFloat(service.quantity) * parseFloat(service.unitRate)) || 0,
        taxableAmount: parseFloat(service.lineValue) || (parseFloat(service.quantity) * parseFloat(service.unitRate)) || 0,
        discount: 0,
        additionalInfo: service.additionalInfo || ''
      }));
    
    setFormData(prev => ({
      ...prev,
      items: transformedItems,
      totalValue: Math.round(totalValue * 100) / 100
    }));

    calculateGST(totalValue);
    
    if (serviceTableRef.current) {
      setCanCreateQuotation(serviceTableRef.current.canCreateInvoice || false);
    }
  };

  const calculateGST = (subtotal) => {
    const result = calculateInvoiceGST({
      subtotal,
      supplierGSTIN: companyGSTIN,
      customerGSTIN: customerGSTIN || formData.customerGSTIN,
      customerCategory: formData.customerCategory || 'Domestic',
      placeOfSupply: formData.placeOfSupply,
      supplierState: companyState,
      gstRate: formData.igstRate || 18,
      cgstRate: formData.cgstRate || 9,
      sgstRate: formData.sgstRate || 9,
      igstRate: formData.igstRate || 18,
    });

    setFormData(prev => ({
      ...prev,
      subtotal: result.subtotal,
      cgstAmount: result.cgstAmount,
      sgstAmount: result.sgstAmount,
      igstAmount: result.igstAmount,
      totalTaxAmount: result.totalTaxAmount,
      totalAmount: result.totalAmount,
    }));
  };

  const breadcrumbItems = [
    {
      label: 'Sales',
      path: '/sales',
      onClick: () => navigate('/sales')
    },
    {
      label: 'Transactions',
      path: '/sales?tab=transactions',
      onClick: () => navigate('/sales?tab=transactions')
    },
    {
      label: 'Sales Quotation',
      path: '/sales/sales-quotation',
      onClick: () => navigate('/sales/sales-quotation')
    },
    {
      label: isApprove ? 'Approve Quotation' : isSend ? 'Send Quotation' : isEdit ? 'Edit Quotation' : isView ? 'View Quotation' : 'Create Quotation',
      path: window.location.pathname,
      isCurrent: true
    }
  ];

  const helpContent = (
    <>
      <h3>What is Sales Quotation?</h3>
      <p>Sales Quotation is a <strong>formal quote</strong> sent to prospects or customers—quotation number, dates, validity, customer details, line items, GST, and terms. It can be approved and then sent to the customer.</p>
      <h3>Tabs</h3>
      <p><strong>Quotation Details</strong> – Quotation number, date, valid until, revision, customer, reference, and line items (add via service selection).</p>
      <p><strong>GST &amp; Totals</strong> – Tax rates and amounts, subtotal, total.</p>
      <p><strong>Notes &amp; Terms</strong> – Notes, terms and conditions, status. After approval you can <strong>Send Quotation</strong> to mark as sent.</p>
      <h3>How to use</h3>
      <p>Select a <strong>customer</strong>, add line items, set validity. Save as Draft. Use Approve to approve the quotation, then Send to mark as sent to the customer.</p>
    </>
  );

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>{isApprove ? 'Approve Sales Quotation' : isSend ? 'Send Sales Quotation' : isEdit ? 'Edit Sales Quotation' : isView ? 'View Sales Quotation' : 'Create Sales Quotation'}</h1>
          <p>{isApprove ? 'Review and approve sales quotation' : isSend ? 'Send quotation to customer' : isEdit ? 'Update sales quotation details' : isView ? 'View sales quotation information' : 'Create new sales quotation for prospects'}</p>
        </div>
        {(!isEdit || isApprove || isSend) && (
          <div className="page-actions" style={{ display: 'flex', gap: '12px' }}>
            {!isView && !isApprove && !isSend && (
              <button 
                type="submit" 
                form="sales-quotation-form"
                className="btn-primary" 
                disabled={loading}
              >
                <Save size={18} />
                {loading ? 'Saving...' : (isEdit ? 'Update Quotation' : 'Create Quotation')}
              </button>
            )}
            {(isApprove || (!isView && isEdit && formData.status === 'Draft')) && (
              <button 
                type="button"
                className="btn-primary" 
                onClick={handleApprove}
                disabled={loading || (isApprove && formData.status !== 'Draft')}
                style={{ backgroundColor: '#10b981', borderColor: '#10b981' }}
              >
                <CheckCircle size={18} />
                {loading ? 'Approving...' : 'Approve Quotation'}
              </button>
            )}
            {(isSend || (!isView && (formData.status === 'Draft' || formData.status === 'Approved'))) && (
              <button 
                type="button"
                className="btn-primary" 
                onClick={handleSend}
                disabled={loading || (isSend && formData.status !== 'Draft' && formData.status !== 'Approved')}
                style={{ backgroundColor: '#3b82f6', borderColor: '#3b82f6' }}
              >
                <Send size={18} />
                {loading ? 'Sending...' : 'Send Quotation'}
              </button>
            )}
            <button className="btn-secondary" onClick={handleCancel}>
              <X size={18} />
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>Sales Quotation Entry</h2>
        </div>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'quotationDetails' ? 'active' : ''}`}
            onClick={() => setActiveTab('quotationDetails')}
          >
            <FileText size={18} />
            <span>Quotation Details</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'gstDetails' ? 'active' : ''}`}
            onClick={() => setActiveTab('gstDetails')}
          >
            <Calculator size={18} />
            <span>GST Details</span>
          </button>
        </div>
          
        <form id="sales-quotation-form" className="form-content" onSubmit={handleSubmit}>
          
          {activeTab === 'quotationDetails' && (
            <div className="tab-content">
              {/* Quotation Information */}
              <div className="section-header">
                <h3>Quotation Information</h3>
              </div>
              
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="quotationNumber" className="form-label required">
                    Quotation #
                  </label>
                  <input
                    type="text"
                    id="quotationNumber"
                    name="quotationNumber"
                    value={formData.quotationNumber || 'Auto-generated on save'}
                    readOnly
                    className="form-input"
                    placeholder="Auto-generated on save"
                    style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed', fontStyle: 'italic' }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="quotationDate" className="form-label required">
                    Quotation Date
                  </label>
                  <input
                    type="date"
                    id="quotationDate"
                    name="quotationDate"
                    value={formData.quotationDate}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isView || isApprove || isSend}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="validUntil" className="form-label required">
                    Valid Until
                  </label>
                  <input
                    type="date"
                    id="validUntil"
                    name="validUntil"
                    value={formData.validUntil}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isView || isApprove || isSend}
                    min={formData.quotationDate}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="customerCategory" className="form-label required">
                    Customer/Sales Category
                  </label>
                  <select
                    id="customerCategory"
                    name="customerCategory"
                    value={formData.customerCategory}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isView || isApprove || isSend}
                  >
                    <option value="">Select</option>
                    <option value="Domestic">Domestic</option>
                    <option value="Export">Export</option>
                    <option value="SEZ">SEZ</option>
                    <option value="Deemed Export">Deemed Export</option>
                  </select>
                </div>
              </div>

              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="customerName" className="form-label required">
                    Customer Name
                  </label>
                  <div className="search-input-wrapper">
                    <input
                      type="text"
                      id="customerName"
                      name="customerName"
                      value={formData.customerName}
                      readOnly
                      className="form-input search-input"
                      placeholder="Select customer"
                      style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
                      disabled={isView || isApprove || isSend}
                    />
                    <button
                      type="button"
                      className="search-button"
                      title="Search Customer"
                      onClick={handleCustomerSearch}
                      disabled={isView || isApprove || isSend}
                    >
                      <Search size={16} />
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="referenceNumber" className="form-label">
                    Reference Number
                  </label>
                  <input
                    type="text"
                    id="referenceNumber"
                    name="referenceNumber"
                    value={formData.referenceNumber}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter customer reference/PO number"
                    disabled={isView || isApprove || isSend}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="customerPaymentTerms" className="form-label required">
                    Payment Terms
                  </label>
                  <select
                    id="customerPaymentTerms"
                    name="customerPaymentTerms"
                    value={formData.customerPaymentTerms}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isView || isApprove || isSend}
                  >
                    <option value="">Select Payment Terms</option>
                    {paymentTermsOptions.map((term) => (
                      <option key={term._id || term.id} value={term._id || term.id}>
                        {term.paymentTermsCode} - {term.description}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="revisionNumber" className="form-label">
                    Revision #
                  </label>
                  <input
                    type="number"
                    id="revisionNumber"
                    name="revisionNumber"
                    value={formData.revisionNumber}
                    onChange={handleInputChange}
                    className="form-input"
                    min="0"
                    disabled={isView || isApprove || isSend}
                    placeholder="0"
                  />
                  {formData.revisionNumber > 0 && (
                    <div style={{ 
                      marginTop: '8px', 
                      fontSize: '12px', 
                      color: '#222448',
                      fontStyle: 'italic'
                    }}>
                      This is revision #{formData.revisionNumber} of quotation {formData.quotationNumber}
                    </div>
                  )}
                </div>
              </div>

              {/* Revision History Section */}
              {formData.id && formData.revisionNumber > 0 && (
                <div className="section-header" style={{ marginTop: '24px' }}>
                  <h3>Revision History</h3>
                </div>
              )}
              {formData.id && formData.revisionNumber > 0 && (
                <div style={{
                  backgroundColor: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '24px'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    marginBottom: '12px'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: '#292752',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '600',
                      fontSize: '14px'
                    }}>
                      R{formData.revisionNumber}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                        Revision #{formData.revisionNumber}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>
                        Current version of this quotation
                      </div>
                    </div>
                  </div>
                  <div style={{ 
                    fontSize: '13px', 
                    color: 'var(--color-text-secondary, #5B6478)',
                    fontStyle: 'italic',
                    paddingTop: '12px',
                    borderTop: '1px solid #e5e7eb'
                  }}>
                    Previous revisions will be displayed here when available
                  </div>
                </div>
              )}

              {/* Service Selection Table */}
              {showServiceTable && (
                <>
                  <div className="section-header" style={{ width: '100%', maxWidth: 'none', marginBottom: '0' }}>
                    <h3>Service Selection</h3>
                    <p>Select services and enter quantities/rates for the selected customer</p>
                  </div>
                  <div style={{ width: '100%', marginTop: '0', marginLeft: '0', marginRight: '0', paddingLeft: '0', paddingRight: '0' }}>
                    <ServiceSelectionTable 
                      ref={serviceTableRef}
                      onServiceDataChange={(updatedServiceData) => {
                        handleServiceDataChange(updatedServiceData)
                        setTimeout(() => {
                          if (serviceTableRef.current) {
                            setCanCreateQuotation(serviceTableRef.current.canCreateInvoice || false)
                          }
                        }, 0)
                      }}
                      initialServiceData={serviceData}
                      isEditMode={isEdit}
                      isViewMode={isView}
                      isApproveMode={isApprove || isSend}
                    />
                  </div>
                </>
              )}

            </div>
          )}

          {activeTab === 'gstDetails' && (
            <div className="tab-content">
              {/* GST Information */}
              <div className="section-header">
                <h3>GST Information</h3>
              </div>

              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="customerGSTIN" className="form-label">
                    GSTIN
                  </label>
                  <input
                    type="text"
                    id="customerGSTIN"
                    name="customerGSTIN"
                    value={formData.customerGSTIN}
                    readOnly
                    className="form-input"
                    placeholder="Enter GSTIN"
                    style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="currency" className="form-label required">
                    Currency
                  </label>
                  <input
                    type="text"
                    id="currency"
                    name="currency"
                    value={formData.currency || ''}
                    readOnly
                    className="form-input"
                    placeholder={formData.currency ? '' : 'Select customer to set currency'}
                    style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>
              </div>

              {/* GST Calculation */}
              <div className="section-header">
                <h3>GST Calculation</h3>
              </div>

              <div className="grid-row-3">
                <div className="form-group">
                  <label htmlFor="cgstRate" className="form-label">
                    CGST Rate (%)
                  </label>
                  <input
                    type="number"
                    id="cgstRate"
                    name="cgstRate"
                    value={formData.cgstRate}
                    readOnly
                    className="form-input"
                    min="0"
                    max="100"
                    step="0.01"
                    style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="sgstRate" className="form-label">
                    SGST Rate (%)
                  </label>
                  <input
                    type="number"
                    id="sgstRate"
                    name="sgstRate"
                    value={formData.sgstRate}
                    readOnly
                    className="form-input"
                    min="0"
                    max="100"
                    step="0.01"
                    style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="igstRate" className="form-label">
                    IGST Rate (%)
                  </label>
                  <input
                    type="number"
                    id="igstRate"
                    name="igstRate"
                    value={formData.igstRate}
                    readOnly
                    className="form-input"
                    min="0"
                    max="100"
                    step="0.01"
                    style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>
              </div>

              {/* Tax Summary */}
              <div className="section-header">
                <h3>Tax Summary</h3>
              </div>

              <div className="grid-row-2">
                <div className="form-group">
                  <label className="form-label">Subtotal</label>
                  <div className="form-input readonly-input">
                    ₹ {formData.subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Total Tax Amount</label>
                  <div className="form-input readonly-input">
                    ₹ {formData.totalTaxAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </div>
                </div>
              </div>

              <div className="grid-row-3">
                <div className="form-group">
                  <label className="form-label">CGST Amount</label>
                  <div className="form-input readonly-input">
                    ₹ {formData.cgstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">SGST Amount</label>
                  <div className="form-input readonly-input">
                    ₹ {formData.sgstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">IGST Amount</label>
                  <div className="form-input readonly-input">
                    ₹ {formData.igstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Total Amount</label>
                <div className="form-input readonly-input" style={{ 
                  fontSize: '18px', 
                  fontWeight: 'bold', 
                  backgroundColor: '#F0EBF7', 
                  borderColor: '#6A4CA2',
                  color: '#51418F'
                }}>
                  ₹ {formData.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </div>
              </div>

              {/* Additional Information */}
              <div className="section-header">
                <h3>Additional Information</h3>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div className="form-group">
                  <label htmlFor="notes" className="form-label">
                    Notes
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    className="form-textarea"
                    placeholder="Additional notes or comments"
                    rows={3}
                    disabled={isView || isApprove || isSend}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="terms" className="form-label">
                    Terms & Conditions
                  </label>
                  <div style={{
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    padding: '16px',
                    backgroundColor: isView || isApprove || isSend ? '#f9fafb' : '#fff',
                    minHeight: '200px'
                  }}>
                    {/* Existing Terms List */}
                    {formData.terms && formData.terms.length > 0 && (
                      <div style={{ marginBottom: '16px' }}>
                        {formData.terms.map((term, index) => (
                          <div 
                            key={index} 
                            style={{ 
                              display: 'flex', 
                              gap: '8px', 
                              marginBottom: '8px',
                              padding: '10px 12px',
                              background: '#f8f9fa',
                              borderRadius: '6px',
                              alignItems: 'flex-start',
                              border: '1px solid #e5e7eb'
                            }}
                          >
                            <span style={{ 
                              color: '#222448', 
                              fontWeight: '600', 
                              marginRight: '8px',
                              marginTop: '2px',
                              fontSize: '14px'
                            }}>•</span>
                            {isView || isApprove || isSend ? (
                              <div style={{ flex: 1, color: '#374151', fontSize: '14px', lineHeight: '1.5' }}>
                                {term}
                              </div>
                            ) : (
                              <>
                                <input
                                  type="text"
                                  value={term}
                                  onChange={(e) => updateTerm(index, e.target.value)}
                                  className="form-input"
                                  style={{ 
                                    flex: 1, 
                                    marginBottom: 0,
                                    padding: '6px 10px',
                                    fontSize: '14px'
                                  }}
                                  placeholder="Enter term or condition"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeTerm(index)}
                                  style={{
                                    padding: '6px 10px',
                                    border: 'none',
                                    background: '#ef4444',
                                    color: '#fff',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: '36px',
                                    height: '36px'
                                  }}
                                  title="Remove this term"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add New Term */}
                    {!isView && !isApprove && !isSend && (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                        <span style={{ 
                          color: '#222448', 
                          fontWeight: '600', 
                          marginTop: '10px',
                          fontSize: '14px'
                        }}>•</span>
                        <input
                          type="text"
                          value={newTerm}
                          onChange={(e) => setNewTerm(e.target.value)}
                          onKeyPress={handleTermKeyPress}
                          className="form-input"
                          placeholder="Enter a new term or condition and press Enter or click Add"
                          style={{ 
                            flex: 1, 
                            marginBottom: 0,
                            padding: '8px 12px',
                            fontSize: '14px'
                          }}
                        />
                        <button
                          type="button"
                          onClick={addTerm}
                          disabled={!newTerm.trim()}
                          style={{
                            padding: '8px 16px',
                            border: 'none',
                            background: newTerm.trim() ? '#292752' : '#9ca3af',
                            color: '#fff',
                            borderRadius: '6px',
                            cursor: newTerm.trim() ? 'pointer' : 'not-allowed',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontWeight: '500',
                            fontSize: '14px',
                            height: '38px'
                          }}
                          title="Add this term"
                        >
                          <Plus size={16} />
                          Add
                        </button>
                      </div>
                    )}

                    {/* Empty State */}
                    {(!formData.terms || formData.terms.length === 0) && (isView || isApprove || isSend) && (
                      <div style={{ 
                        textAlign: 'center', 
                        color: 'var(--color-text-muted, #8B93A7)', 
                        padding: '20px',
                        fontSize: '14px'
                      }}>
                        No terms and conditions added
                      </div>
                    )}
                  </div>
                  <div style={{ 
                    marginTop: '8px', 
                    fontSize: '12px', 
                    color: 'var(--color-text-secondary, #5B6478)',
                    fontStyle: 'italic'
                  }}>
                    Each term will appear as a bullet point on the quotation document
                  </div>
                </div>
              </div>
            </div>
          )}

        </form>

        {/* Bottom Section: Total Value, Remark, and Action Buttons */}
        <div className="form-actions" style={{ 
          display: 'flex', 
          gap: '20px', 
          alignItems: 'end',
          marginTop: '20px',
          padding: '20px',
          backgroundColor: '#f8fafc',
          borderTop: '1px solid #e2e8f0',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', gap: '20px', alignItems: 'end' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" style={{ color: '#222448', fontWeight: '600' }}>
                Total Value:
              </label>
              <input
                type="text"
                value={formData.totalValue ? formData.totalValue.toFixed(2) : '0.00'}
                readOnly
                className="form-input"
                style={{ 
                  backgroundColor: '#f9fafb', 
                  color: 'var(--color-text-secondary, #5B6478)', 
                  cursor: 'not-allowed',
                  textAlign: 'right',
                  fontWeight: '600',
                  width: '150px'
                }}
              />
            </div>
            
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" style={{ color: '#222448', fontWeight: '600' }}>
                Remark:
              </label>
              <input
                type="text"
                name="remark"
                value={formData.remark || ''}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter remarks..."
                disabled={isView || isApprove || isSend}
                style={{ width: '250px' }}
              />
            </div>
          </div>

          {!isApprove && !isSend && (
            <div style={{ display: 'flex', gap: '12px' }}>
              <button 
                type="button" 
                className="btn-secondary" 
                onClick={() => {
                  window.location.reload();
                }}
                disabled={isView}
              >
                Reset
              </button>
              
              <button 
                type="button" 
                className="btn-secondary" 
                onClick={() => {
                  if (serviceTableRef.current && serviceTableRef.current.handleEsc) {
                    serviceTableRef.current.handleEsc()
                    setTimeout(() => {
                      setCanCreateQuotation(false)
                    }, 100)
                  }
                }}
                disabled={isView}
                title="Load all available services from service master"
              >
                Add More Services
              </button>
              
              <button 
                type="button" 
                className="btn-secondary"
                onClick={() => {
                  if (serviceTableRef.current && serviceTableRef.current.handlePreview) {
                    serviceTableRef.current.handlePreview()
                    setTimeout(() => {
                      if (serviceTableRef.current) {
                        setCanCreateQuotation(serviceTableRef.current.canCreateInvoice || false)
                      }
                    }, 100)
                  }
                }}
                disabled={isView}
                title="Filter the table to show only services with quantity and rate entered"
              >
                Preview Selected
              </button>
              
              {!isView && (
                <button 
                  type="button" 
                  className="btn-primary" 
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? 'Creating...' : (isEdit ? 'Update Quotation' : 'Create Quotation')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Customer Selection Modal */}
      <CustomerSelectionModal
        isOpen={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
        onSelectCustomer={handleCustomerSelect}
        includeProspects={true}
      />

      {/* Approve Confirmation Modal */}
      <ConfirmationModal
        isOpen={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        onConfirm={confirmApprove}
        title="Approve Sales Quotation"
        message={`Are you sure you want to approve quotation ${formData.quotationNumber}? This action cannot be undone.`}
        confirmText="Approve Quotation"
        cancelText="Cancel"
        type="success"
      />

      {/* Send Confirmation Modal */}
      <ConfirmationModal
        isOpen={showSendModal}
        onClose={() => setShowSendModal(false)}
        onConfirm={confirmSend}
        title="Send Sales Quotation"
        message={`Are you sure you want to send quotation ${formData.quotationNumber} to the customer? This will change the status to 'Sent'.`}
        confirmText="Send Quotation"
        cancelText="Cancel"
        type="info"
      />

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Sales Quotation – Help">
        {helpContent}
      </HelpModal>
    </div>
  );
};

export default SalesQuotationForm;

